/**
 * Tappy Labs Brand Identity System
 * Complete design system for Learning Aquarium platform
 * Integrates Morpheus-RL technology with transparent learning aesthetics
 */

export const TappyLabsBranding = {
  
  // ============================================================================
  // CORE BRAND IDENTITY
  // ============================================================================
  
  brand: {
    name: "Tappy Labs",
    tagline: "Master the Science of Transparent Learning",
    mission: "Transforming AI education through Tappology - the scientific study of learning ecosystem optimization using Morpheus-RL technology",
    positioning: "The first platform teaching parents the science of transparent learning"
  },

  // ============================================================================
  // COLOR PALETTE - Learning Aquarium Theme
  // ============================================================================
  
  colors: {
    // Primary Palette - Learning Aquarium Core
    primary: {
      learningAquariumBlue: "#0EA5E9",     // Crystal clear water - Sky Blue 500
      confidenceFishGold: "#F59E0B",       // Achievement moments - Amber 500
      deepOcean: "#0F172A",                // Professional depth - Slate 900
      clarityWhite: "#FAFAFA",             // Transparent backgrounds - Neutral 50
    },

    // Secondary Palette - Ecosystem Health
    secondary: {
      ecosystemGreen: "#10B981",           // Healthy learning growth - Emerald 500
      breakthroughPurple: "#8B5CF6",       // Expert certification - Violet 500
      gentleCoral: "#F97316",              // Engagement highlights - Orange 500
    },

    // Semantic Colors - Learning States
    semantic: {
      success: "#10B981",                  // Ecosystem Green
      warning: "#F59E0B",                  // Confidence Fish Gold
      error: "#EF4444",                    // Red 500
      info: "#0EA5E9",                     // Learning Aquarium Blue
      neutral: "#64748B",                  // Slate 500
    },

    // Gradient Definitions
    gradients: {
      aquariumDepth: "linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)",
      confidenceGlow: "linear-gradient(135deg, #F59E0B 0%, #F97316 100%)",
      expertCertification: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
      crystalClear: "linear-gradient(135deg, #FAFAFA 0%, #F1F5F9 100%)",
      ecosystemFlow: "linear-gradient(135deg, #10B981 0%, #0EA5E9 100%)",
    },

    // Transparency Levels
    transparency: {
      glass: "rgba(255, 255, 255, 0.1)",
      overlay: "rgba(15, 23, 42, 0.8)",
      subtle: "rgba(14, 165, 233, 0.05)",
      medium: "rgba(14, 165, 233, 0.2)",
      strong: "rgba(14, 165, 233, 0.4)",
    }
  },

  // ============================================================================
  // TYPOGRAPHY SYSTEM - Scientific Precision + Accessibility
  // ============================================================================
  
  typography: {
    // Font Families
    fonts: {
      headers: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      technical: "'JetBrains Mono', 'SF Mono', Monaco, monospace",
      display: "'Inter', sans-serif",
    },

    // Type Scale - Modular scale based on 1.25 ratio
    scale: {
      xs: "0.75rem",     // 12px
      sm: "0.875rem",    // 14px
      base: "1rem",      // 16px
      lg: "1.125rem",    // 18px
      xl: "1.25rem",     // 20px
      "2xl": "1.5rem",   // 24px
      "3xl": "1.875rem", // 30px
      "4xl": "2.25rem",  // 36px
      "5xl": "3rem",     // 48px
      "6xl": "3.75rem",  // 60px
    },

    // Font Weights
    weights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },

    // Line Heights
    lineHeights: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2,
    }
  },

  // ============================================================================
  // COMPONENT STYLES - Learning Aquarium Components
  // ============================================================================
  
  components: {
    // Glass Morphism Cards - Aquarium containers
    glassCard: {
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "16px",
      boxShadow: "0 8px 32px rgba(15, 23, 42, 0.1)",
    },

    // Confidence Fish Indicators
    confidenceFish: {
      background: "linear-gradient(135deg, #F59E0B 0%, #F97316 100%)",
      borderRadius: "50%",
      boxShadow: "0 4px 16px rgba(245, 158, 11, 0.3)",
      animation: "swim 3s ease-in-out infinite",
    },

    // Learning Aquarium Container
    aquariumContainer: {
      background: "linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)",
      borderRadius: "24px",
      border: "2px solid rgba(255, 255, 255, 0.2)",
      boxShadow: "0 16px 64px rgba(14, 165, 233, 0.2)",
      overflow: "hidden",
    },

    // Expert Certification Badge
    expertBadge: {
      background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
      color: "#FFFFFF",
      borderRadius: "12px",
      padding: "8px 16px",
      fontWeight: 600,
      fontSize: "0.875rem",
      boxShadow: "0 4px 16px rgba(139, 92, 246, 0.3)",
    },

    // Tappology Science Indicators
    tappologyIndicator: {
      background: "rgba(16, 185, 129, 0.1)",
      border: "1px solid rgba(16, 185, 129, 0.3)",
      borderRadius: "8px",
      color: "#10B981",
      fontSize: "0.75rem",
      fontWeight: 500,
      padding: "4px 8px",
    },

    // Morpheus-RL Technical Display
    morpheusRLDisplay: {
      background: "#0F172A",
      border: "1px solid rgba(100, 116, 139, 0.3)",
      borderRadius: "8px",
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "0.875rem",
      color: "#E2E8F0",
      padding: "16px",
    },

    // Button Variants
    buttons: {
      primary: {
        background: "linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)",
        color: "#FFFFFF",
        border: "none",
        borderRadius: "12px",
        padding: "12px 24px",
        fontWeight: 600,
        boxShadow: "0 4px 16px rgba(14, 165, 233, 0.3)",
        transition: "all 0.2s ease",
      },
      
      secondary: {
        background: "rgba(255, 255, 255, 0.1)",
        color: "#0EA5E9",
        border: "1px solid rgba(14, 165, 233, 0.3)",
        borderRadius: "12px",
        padding: "12px 24px",
        fontWeight: 600,
        backdropFilter: "blur(10px)",
        transition: "all 0.2s ease",
      },

      expert: {
        background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
        color: "#FFFFFF",
        border: "none",
        borderRadius: "12px",
        padding: "12px 24px",
        fontWeight: 600,
        boxShadow: "0 4px 16px rgba(139, 92, 246, 0.3)",
        transition: "all 0.2s ease",
      }
    }
  },

  // ============================================================================
  // ANIMATIONS - Learning Ecosystem Motion
  // ============================================================================
  
  animations: {
    // CSS Keyframes for learning aquarium effects
    keyframes: {
      swim: `
        @keyframes swim {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          25% { transform: translateX(10px) rotate(5deg); }
          50% { transform: translateX(0) rotate(0deg); }
          75% { transform: translateX(-10px) rotate(-5deg); }
        }
      `,
      
      float: `
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `,
      
      shimmer: `
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `,
      
      pulse: `
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `,

      bubbleRise: `
        @keyframes bubbleRise {
          0% { 
            transform: translateY(100vh) scale(0); 
            opacity: 0; 
          }
          10% { 
            opacity: 1; 
          }
          90% { 
            opacity: 1; 
          }
          100% { 
            transform: translateY(-100px) scale(1); 
            opacity: 0; 
          }
        }
      `
    },

    // Duration and Easing
    durations: {
      fast: "0.15s",
      normal: "0.2s",
      slow: "0.3s",
      slower: "0.5s",
    },

    easing: {
      ease: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      easeOut: "cubic-bezier(0, 0, 0.2, 1)",
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    }
  },

  // ============================================================================
  // THEME UTILITIES - Helper Functions
  // ============================================================================
  
  utils: {
    // Generate CSS custom properties
    generateCSSVariables() {
      const cssVars = {};
      
      // Colors
      Object.entries(this.colors.primary).forEach(([key, value]) => {
        cssVars[`--color-${key}`] = value;
      });
      
      Object.entries(this.colors.secondary).forEach(([key, value]) => {
        cssVars[`--color-${key}`] = value;
      });

      return cssVars;
    },

    // Get component style with variants
    getComponentStyle(component, variant = 'default') {
      const baseStyle = this.components[component];
      if (variant === 'default') return baseStyle;
      
      return {
        ...baseStyle,
        ...this.components[component]?.[variant]
      };
    }
  }
};

// Export individual sections for easier imports
export const { colors, typography, components, animations } = TappyLabsBranding;

// Default export
export default TappyLabsBranding;